import { IMAGE, MAX_VIDEO_DURATION, STOCK_CLIP, TEXT_OVERLAY, VIDEO } from '@/constants';
import { ThemeProp, TributeVideoAsset } from '@/types';

export const getAssetsTotalDuration = (assets: TributeVideoAsset[]): number => {
  return assets.reduce((total, { asset }) => total + (asset?.duration ?? 0), 0);
};

export const getCombinedVideosDuration = (videos: TributeVideoAsset[]): number => {
  return videos.reduce((total, { asset }) => {
    const videoDuration = asset?.duration ?? 0;
    return total + Math.min(videoDuration, MAX_VIDEO_DURATION);
  }, 0);
};

export const getRemainingDuration = (
  totalAudioDuration: number,
  themeDuration: number,
  combinedVideosDuration: number
): number => {
  return totalAudioDuration - themeDuration - combinedVideosDuration;
};

export const getDurationMultiplierSum = (assets: TributeVideoAsset[]): number => {
  return assets.reduce(
    (sum, currentAsset) =>
      sum +
      (currentAsset.durationMultiplier && currentAsset.durationMultiplier > 0
        ? currentAsset.durationMultiplier
        : 1),
    0
  );
};

export const getDurationOfAssets = (
  photos: TributeVideoAsset[],
  textOverlays: TributeVideoAsset[],
  remainingDuration: number,
  minimumDuration = 5
): number => {
  if (photos.length === 0 && textOverlays.length === 0) {
    return minimumDuration;
  }

  const totalPhotosDuration = getDurationMultiplierSum(photos);
  const totalTextOverlaysDuration = getDurationMultiplierSum(textOverlays);
  const totalDurationMultiplier = totalPhotosDuration + totalTextOverlaysDuration;

  if (totalDurationMultiplier === 0) {
    return minimumDuration;
  }

  return Math.round(remainingDuration / totalDurationMultiplier);
};

export const filterAssetsByType = (assets: TributeVideoAsset[], type: string) =>
  assets?.filter((asset) => asset.asset.assetType === type);

export const filterAssetsByMultipleTypes = (assets: TributeVideoAsset[], types: string[]) =>
  assets?.filter((asset) => types.includes(asset.asset.assetType));

export const getTributeAssetsDurationValidation = (
  photos?: TributeVideoAsset[],
  songs?: TributeVideoAsset[],
  theme?: ThemeProp | null
): boolean => {
  if (!theme) {
    return true;
  }
  const audioAssets = songs ?? [];
  if (audioAssets.length === 0) {
    return true;
  }
  const photosAssets = photos ?? [];
  const videoAssets = filterAssetsByMultipleTypes(photosAssets, [VIDEO, STOCK_CLIP]);
  const photoAssets = filterAssetsByType(photosAssets, IMAGE);
  const textOverlayAssets = filterAssetsByType(photosAssets, TEXT_OVERLAY);

  const totalAudioDuration = getAssetsTotalDuration(audioAssets);
  const combinedVideosDuration = getCombinedVideosDuration(videoAssets);
  const remainingDuration = getRemainingDuration(
    totalAudioDuration,
    theme?.duration ?? 0,
    combinedVideosDuration
  );
  const durationOfAssets = getDurationOfAssets(photoAssets, textOverlayAssets, remainingDuration);

  return durationOfAssets >= 5 && remainingDuration >= 0;
};
